<template>
  <div class="c1mc-wrap">

    <!-- Nav row -->
    <div class="c1mc-nav">
      <button class="c1mc-nav-btn" @click="prev">&#8249;</button>
      <span class="c1mc-label">{{ navLabel }}</span>
      <button class="c1mc-nav-btn" @click="next">&#8250;</button>
      <div class="c1mc-view-wrap">
        <select class="c1mc-view-sel" v-model="viewMode">
          <option value="W">Week</option>
          <option value="M">Month</option>
        </select>
        <svg class="c1mc-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <button class="c1mc-reset-btn" @click="reset" title="Clear selection">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
      </button>
    </div>

    <!-- Week view -->
    <div v-if="viewMode === 'W'" class="c1mc-week-grid">
      <div v-for="day in weekDays" :key="day.date" class="c1mc-day"
        :class="{ today: day.isToday, selected: selectedDate === day.date }"
        @click="selectDay(day.date)">
        <div class="c1mc-day-hdr">
          <span class="c1mc-dow">{{ day.dow }}</span>
          <span class="c1mc-dd" :class="{ 'c1mc-dd-today': day.isToday }">{{ day.dd }}</span>
        </div>
        <div class="c1mc-bar-wrap">
          <div class="c1mc-bar" :style="{ height: barH(day.date) + 'px', opacity: dayTotal(day.date) > 0 ? 1 : 0.15 }"></div>
        </div>
        <div class="c1mc-day-cnt" v-if="dayTotal(day.date) > 0">{{ dayTotal(day.date) }}</div>
      </div>
    </div>

    <!-- Month view -->
    <div v-else class="c1mc-month-grid">
      <div class="c1mc-mhdr" v-for="h in ['Mo','Tu','We','Th','Fr','Sa','Su']" :key="h">{{ h }}</div>
      <div v-for="(cell, i) in monthCells" :key="'mc' + i" class="c1mc-mcell"
        :class="{ 'c1mc-mcell-empty': !cell, 'c1mc-mcell-today': cell && cell.isToday, 'c1mc-mcell-sel': cell && selectedDate === cell.date }"
        @click="cell ? selectDay(cell.date) : null">
        <template v-if="cell">
          <span class="c1mc-mdd" :class="{ 'c1mc-mdd-today': cell.isToday }">{{ cell.dd }}</span>
          <div v-if="dayTotal(cell.date) > 0" class="c1mc-mdot" :title="dayTotal(cell.date) + ' items'"></div>
        </template>
      </div>
    </div>

    <!-- Selected day count pill -->
    <div v-if="selectedDate && dayTotal(selectedDate) > 0" class="c1mc-sel-pill">
      {{ fmtFull(selectedDate) }} · {{ dayTotal(selectedDate) }} {{ itemLabel }}
      <span class="c1mc-sel-x" @click.stop="reset">✕</span>
    </div>

  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'C1ModCalendar',
  props: {
    items:      { type: Array,  default: () => [] },
    dateField:  { type: String, default: 'created_at' },
    itemLabel:  { type: String, default: 'items' },
  },
  data() {
    return {
      viewMode:    'W',
      anchor:      moment().startOf('isoWeek'),
      selectedDate: null,
    };
  },
  computed: {
    navLabel() {
      if (this.viewMode === 'W') {
        const s = this.anchor.clone().startOf('isoWeek');
        const e = s.clone().endOf('isoWeek');
        return s.format('MMM D') + ' – ' + e.format('D, YYYY');
      }
      return this.anchor.clone().startOf('month').format('MMMM YYYY');
    },
    weekDays() {
      const start = this.anchor.clone().startOf('isoWeek');
      const today = moment().format('YYYY-MM-DD');
      return Array.from({ length: 7 }, (_, i) => {
        const d = start.clone().add(i, 'days');
        const date = d.format('YYYY-MM-DD');
        return { date, dow: d.format('ddd'), dd: d.format('D'), isToday: date === today };
      });
    },
    monthCells() {
      const mStart = this.anchor.clone().startOf('month');
      const mEnd   = mStart.clone().endOf('month');
      const today  = moment().format('YYYY-MM-DD');
      const cells  = [];
      // Leading blanks (Mon = 1 ... offset)
      const startDow = mStart.isoWeekday(); // 1=Mon
      for (let i = 1; i < startDow; i++) cells.push(null);
      for (let d = mStart.clone(); d.isSameOrBefore(mEnd, 'day'); d.add(1, 'day')) {
        const date = d.format('YYYY-MM-DD');
        cells.push({ date, dd: d.format('D'), isToday: date === today });
      }
      // Trailing blanks to fill last row
      while (cells.length % 7 !== 0) cells.push(null);
      return cells;
    },
    // Map: 'YYYY-MM-DD' → count
    dayMap() {
      const map = {};
      for (const item of this.items) {
        const raw = item[this.dateField];
        if (!raw) continue;
        const key = moment(raw).format('YYYY-MM-DD');
        map[key] = (map[key] || 0) + 1;
      }
      return map;
    },
    maxDay() {
      return Math.max(1, ...Object.values(this.dayMap));
    },
  },
  methods: {
    dayTotal(date) {
      return this.dayMap[date] || 0;
    },
    barH(date) {
      const cnt = this.dayTotal(date);
      if (!cnt) return 4;
      return Math.max(4, Math.round((cnt / this.maxDay) * 44));
    },
    selectDay(date) {
      if (this.selectedDate === date) {
        this.selectedDate = null;
        this.$emit('filter-date', null);
      } else {
        this.selectedDate = date;
        this.$emit('filter-date', date);
      }
    },
    prev() {
      this.anchor = this.anchor.clone().subtract(1, this.viewMode === 'W' ? 'week' : 'month');
    },
    next() {
      this.anchor = this.anchor.clone().add(1, this.viewMode === 'W' ? 'week' : 'month');
    },
    reset() {
      this.selectedDate = null;
      this.$emit('filter-date', null);
    },
    fmtFull(date) {
      return moment(date).format('ddd, MMM D');
    },
  },
};
</script>

<style scoped>
.c1mc-wrap {
  padding: 10px 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* NAV */
.c1mc-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.c1mc-label {
  flex: 1;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--c1-t1);
  white-space: nowrap;
}
.c1mc-nav-btn {
  width: 26px; height: 26px;
  border: 1px solid var(--c1-brd);
  border-radius: 6px;
  background: var(--c1-card);
  color: var(--c1-t2);
  font-size: 16px; line-height: 1;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .12s;
}
.c1mc-nav-btn:hover { background: var(--c1-act); }
.c1mc-view-wrap {
  position: relative;
  display: flex; align-items: center;
}
.c1mc-view-sel {
  appearance: none;
  border: 1px solid var(--c1-brd);
  border-radius: 6px;
  background: var(--c1-card);
  color: var(--c1-t2);
  font-size: 11px; font-weight: 600;
  padding: 4px 22px 4px 8px;
  cursor: pointer;
  font-family: inherit;
}
.c1mc-arr {
  position: absolute; right: 5px;
  width: 11px; height: 11px;
  stroke: var(--c1-t3);
  pointer-events: none;
}
.c1mc-reset-btn {
  width: 26px; height: 26px;
  border: 1px solid var(--c1-brd);
  border-radius: 6px;
  background: var(--c1-card);
  color: var(--c1-t3);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .12s;
  flex-shrink: 0;
}
.c1mc-reset-btn:hover { background: var(--c1-act); color: var(--c1-t1); }
.c1mc-reset-btn svg { width: 12px; height: 12px; }

/* WEEK GRID */
.c1mc-week-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.c1mc-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 5px 2px 6px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background .12s, border-color .12s;
}
.c1mc-day:hover { background: var(--c1-act); }
.c1mc-day.today { border-color: var(--c1-dp); }
.c1mc-day.selected { background: rgba(124,92,252,.08); border-color: var(--c1-dp); }
.c1mc-day-hdr {
  display: flex; flex-direction: column; align-items: center; gap: 1px;
}
.c1mc-dow { font-size: 8px; font-weight: 700; color: var(--c1-t3); text-transform: uppercase; }
.c1mc-dd { font-size: 12px; font-weight: 700; color: var(--c1-t2); }
.c1mc-dd-today { color: var(--c1-dp); }
.c1mc-bar-wrap {
  width: 100%; display: flex; align-items: flex-end; justify-content: center;
  height: 44px;
}
.c1mc-bar {
  width: 70%; border-radius: 4px 4px 0 0;
  background: var(--c1-dp);
  min-height: 4px;
  transition: height .2s ease;
}
.c1mc-day-cnt {
  font-size: 9px; font-weight: 700; color: var(--c1-dp);
}

/* MONTH GRID */
.c1mc-month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}
.c1mc-mhdr {
  font-size: 8px; font-weight: 700; color: var(--c1-t3);
  text-align: center; padding: 3px 0;
  text-transform: uppercase;
}
.c1mc-mcell {
  min-height: 32px;
  display: flex; flex-direction: column; align-items: center;
  justify-content: flex-start;
  gap: 3px;
  padding: 4px 2px;
  border-radius: 5px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background .1s;
}
.c1mc-mcell:hover:not(.c1mc-mcell-empty) { background: var(--c1-act); }
.c1mc-mcell-empty { cursor: default; }
.c1mc-mcell-today { border-color: var(--c1-dp); }
.c1mc-mcell-sel { background: rgba(124,92,252,.08); border-color: var(--c1-dp); }
.c1mc-mdd { font-size: 11px; font-weight: 600; color: var(--c1-t2); }
.c1mc-mdd-today { color: var(--c1-dp); font-weight: 800; }
.c1mc-mdot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--c1-dp);
}

/* SELECTION PILL */
.c1mc-sel-pill {
  display: flex; align-items: center; gap: 6px;
  padding: 5px 10px;
  background: rgba(124,92,252,.08);
  border: 1px solid rgba(124,92,252,.2);
  border-radius: 8px;
  font-size: 11px; font-weight: 600; color: var(--c1-dp);
}
.c1mc-sel-x {
  margin-left: auto; cursor: pointer; opacity: .6;
  font-size: 11px;
}
.c1mc-sel-x:hover { opacity: 1; }
</style>
